/**
 * Optimizely Debug Utility
 *
 * Tracks all experiment decisions and exposes them for debugging.
 * Access in browser console: window.__OPTIMIZELY_DEBUG__
 *
 * Usage:
 *   // In component:
 *   import { useDecision } from '@optimizely/react-sdk';
 *   import { trackDecision } from 'lib/optimizelyDebug';
 *
 *   const [decision] = useDecision(flagKey);
 *   trackDecision(flagKey, decision);
 *
 *   // Or use the wrapper hook:
 *   import { useTrackedDecision } from 'lib/optimizelyDebug';
 *   const [decision] = useTrackedDecision('my_flag');
 *
 *   // In browser console:
 *   window.__OPTIMIZELY_DEBUG__.getDecisions()
 *   window.__OPTIMIZELY_DEBUG__.log()
 *   window.__OPTIMIZELY_DEBUG__.clear()
 */

import { useDecision } from "@optimizely/react-sdk";

const STORAGE_KEY = "__OPTIMIZELY_DEBUG__";

// Initialize debug store on window
if (typeof globalThis.window !== "undefined" && !globalThis[STORAGE_KEY]) {
	globalThis[STORAGE_KEY] = {
		decisions: {},

		/**
		 * Get all tracked decisions
		 */
		getDecisions() {
			return this.decisions;
		},

		/**
		 * Log all decisions to console in a readable format
		 */
		log() {
			console.group("🧪 Optimizely Decisions");
			const entries = Object.entries(this.decisions);
			if (entries.length === 0) {
				console.log("No decisions tracked yet");
			} else {
				entries.forEach(([flagKey, data]) => {
					const status = data.enabled ? "✅ ENABLED" : "⛔ DISABLED";
					const variation = data.variationKey || "default";
					console.log(
						`%c${flagKey}%c ${status} (${variation})`,
						"font-weight: bold; color: #6366f1",
						data.enabled ? "color: #22c55e" : "color: #ef4444"
					);
					if (data.reasons?.length > 0) {
						console.log("  Reasons:", data.reasons);
					}
					if (Object.keys(data.variables || {}).length > 0) {
						console.log("  Variables:", data.variables);
					}
				});
			}
			console.groupEnd();
			return this.decisions;
		},

		/**
		 * Clear all tracked decisions
		 */
		clear() {
			this.decisions = {};
			console.log("🧹 Optimizely debug cleared");
		}
	};
}

/**
 * Track a decision for debugging
 *
 * @param {string} flagKey - The flag/experiment key
 * @param {Object} decision - The decision object from useDecision
 */
export function trackDecision(flagKey, decision) {
	if (typeof globalThis.window === "undefined") return;

	const debugData = {
		enabled: decision?.enabled ?? false,
		variationKey: decision?.variationKey || null,
		variables: decision?.variables || {},
		reasons: decision?.reasons || [],
		timestamp: new Date().toISOString()
	};

	globalThis[STORAGE_KEY].decisions[flagKey] = debugData;

	// Auto-log in development
	if (process.env.NODE_ENV === "development") {
		const status = debugData.enabled ? "✅" : "⛔";
		console.log(
			`[Optimizely] ${status} ${flagKey}: ${debugData.variationKey || "default"}`,
			debugData.reasons?.length ? `(${debugData.reasons.join(", ")})` : ""
		);
	}
}

/**
 * Custom hook that tracks decisions automatically.
 * Use this instead of useDecision directly for automatic debug tracking.
 *
 * @param {string} flagKey - The flag/experiment key
 * @param {Object} options - Options to pass to useDecision
 * @returns {Array} Same return value as useDecision [decision, clientReady, didTimeout]
 *
 * @example
 * import { useTrackedDecision } from 'lib/optimizelyDebug';
 *
 * function MyComponent() {
 *   const [decision] = useTrackedDecision('my_feature_flag');
 *   return decision.enabled ? <NewFeature /> : <OldFeature />;
 * }
 */
export function useTrackedDecision(flagKey, options) {
	const result = useDecision(flagKey, options);
	const [decision] = result;

	// Track on client side only (useEffect would be better but this is simpler)
	if (typeof globalThis.window !== "undefined") {
		trackDecision(flagKey, decision);
	}

	return result;
}

export default trackDecision;
